/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useRef } from 'preact/hooks';
import { Range, pan } from '../utils/panUtils';
import { KbPanEvent } from '../utils/keyboardUtils';

type UseKbPanOptions = {
  width: number;
  height: number;
  getRangeX: () => Range;
  getRangeY: () => Range;
  globalRangeX: Range;
  globalRangeY: Range;
  onRangeXChange: (detail: Range) => void;
  onRangeYChange: (detail: Range) => void;
  isDisabled?: boolean;
};

/**
 * Hook that enables and handles keyboard panning gesture.
 */
const useKbPan = ({
  getRangeX,
  getRangeY,
  globalRangeX,
  globalRangeY,
  onRangeXChange,
  onRangeYChange,
  isDisabled = false
}: UseKbPanOptions) => {
  const prevRangeX = useRef({ min: 0, max: 0 });
  const prevRangeY = useRef({ min: 0, max: 0 });

  const onKbPanHorizontal = (e: KbPanEvent) => {
    const {
      detail: { rangeRatio }
    } = e;
    const { newRange: newRangeX } = pan(
      rangeRatio,
      getRangeX(),
      prevRangeX.current,
      globalRangeX,
      onRangeXChange
    );
    prevRangeX.current = newRangeX;
  };

  const onKbPanVertical = (e: KbPanEvent) => {
    const {
      detail: { rangeRatio }
    } = e;
    const { newRange: newRangeY } = pan(
      rangeRatio,
      getRangeY(),
      prevRangeY.current,
      globalRangeY,
      onRangeYChange
    );
    prevRangeY.current = newRangeY;
  };

  return isDisabled
    ? {}
    : {
        onKbPanHorizontal,
        onKbPanVertical
      };
};

export { useKbPan };
